import { motion } from "framer-motion";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLogin } from "../../contexts/LoginContext";
import { gerarLinkWhatsApp } from "../../utils/WhatsappLink";

const cardVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.08, duration: 0.4, ease: "easeOut" },
    }),
};

const cardSx = {
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    p: { xs: 2, md: 3 },
};

const moeda = (valor) => (Number(valor) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// status: "sim" = incluso, "limitado" = incluso com restrição, "nao" = não incluso
const ItemBeneficio = ({ label, status, escuro }) => {
    const icones = {
        sim: <CheckCircleRoundedIcon sx={{ fontSize: 16, color: escuro ? "#4ade80" : "#16a34a" }} />,
        limitado: <RemoveCircleOutlineRoundedIcon sx={{ fontSize: 16, color: escuro ? "#fcd34d" : "#d97706" }} />,
        nao: <CancelOutlinedIcon sx={{ fontSize: 16, color: escuro ? "#64748b" : "#d1d5db" }} />,
    };
    const corTexto = status === "nao"
        ? (escuro ? "#64748b" : "#9ca3af")
        : (escuro ? "#e2e8f0" : "#374151");

    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ display: "flex", mt: "1px", flexShrink: 0 }}>{icones[status] || icones.sim}</Box>
            <Typography sx={{ fontSize: 12, color: corTexto, lineHeight: 1.45, textDecoration: status === "nao" ? "line-through" : "none" }}>
                {label}
            </Typography>
        </Box>
    );
};

const Plans = (props) => {
    const token = localStorage.getItem("token");

    const [hasPlan, setHasPlan] = useState(false);

    const { fetchLogin } = useLogin()

    useEffect(() => {
        const checkHasPlan = async () => {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/hasPlan`, {
                headers: {
                    Authorization: `${token}`
                }
            });
            if (response.data.status === 200) {
                setHasPlan(true)
            } else {
                setHasPlan(false)
            }
        }
        checkHasPlan()
    }, [token])

    const navigate = useNavigate();

    const [objPlanos, setObjPlanos] = useState([]);

    useEffect(() => {
        const getPlanos = async () => {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/listarPlanos`);

            setObjPlanos(response.data.planos);
            console.log(response.data.planos);
        }
        getPlanos();
    }, [])

    const plans = [
        {
            id: objPlanos[0]?.idPlano,
            title: objPlanos[0]?.nome,
            chips: [
                { label: "Faturamento da PJ até: R$ 20.000/mês", status: "sim" },
                { label: "Composição societária: até 1 sócio", status: "sim" },
                { label: "Atuação junto a planos de saúde (sob demanda): envio de NF por e-mail e anexos em portais", status: "sim" },
                { label: "Obrigações fiscais/contábeis: dentro do Simples Nacional", status: "sim" },
                { label: "Certificado digital incluso: apenas no plano anual", status: "sim" },
                { label: "Regime tributário: Simples Nacional", status: "sim" },
                { label: "IRPF sob demanda: até 1 sócio incluso", status: "limitado" },
                { label: "Emissão via Clarea Fácil: até 3 NF/mês", status: "limitado" },
                { label: "Apenas pró-labore do sócio", status: "limitado" },
                { label: "Emissões/mês feitas pela equipe Clarea", status: "nao" },
                { label: "Folha de pagamento de colaboradores", status: "nao" },
                { label: "50% de desconto em planejamento patrimonial para até 3 sócios (somente no plano anual)", status: "nao" },
            ],
            description: objPlanos[0]?.descricao,
            priceOld: objPlanos[0]?.valorAntigoMensal ? Number(objPlanos[0]?.valorAntigoMensal) : "",
            discount: objPlanos[0]?.descontoMensal ? Number(objPlanos[0]?.descontoMensal) : "",
            priceNew: Number(objPlanos[0]?.valorNovoMensal),
            priceOldYear: objPlanos[0]?.valorAntigoAnual ? Number(objPlanos[0]?.valorAntigoAnual) : "",
            priceNewYear: objPlanos[0]?.valorNovoAnual ? Number(objPlanos[0]?.valorNovoAnual) : "",
            discountYear: objPlanos[0]?.descontoAnual ? Number(objPlanos[0]?.descontoAnual) : "",

        },
        {
            id: objPlanos[1]?.idPlano,
            title: objPlanos[1]?.nome,
            chips: [
                { label: "Faturamento da PJ até: R$ 60.000/mês", status: "sim" },
                { label: "Composição societária: até 2 sócios", status: "sim" },
                { label: "Atuação junto a planos de saúde (sob demanda): envio de NF por e-mail e anexos em portais", status: "sim" },
                { label: "Obrigações fiscais/contábeis: completas dentro do Simples Nacional", status: "sim" },
                { label: "Certificado digital incluso: apenas no plano anual", status: "sim" },
                { label: "Regime tributário: Simples Nacional", status: "sim" },
                { label: "IRPF sob demanda: até 2 sócios inclusos", status: "sim" },
                { label: "Emissão via Clarea Fácil: ilimitada", status: "sim" },
                { label: "Pró-labore dos sócios", status: "sim" },
                { label: "Emissões/mês feitas pela equipe Clarea: até 10", status: "sim" },
                { label: "Folha de pagamento até 2 colaboradores", status: "sim" },
                { label: "50% de desconto em planejamento patrimonial para até 3 sócios (somente no plano anual)", status: "nao" },
            ],
            description: objPlanos[1]?.descricao,
            priceOld: objPlanos[1]?.valorAntigoMensal ? Number(objPlanos[1]?.valorAntigoMensal) : "",
            discount: objPlanos[1]?.descontoMensal ? Number(objPlanos[1]?.descontoMensal) : "",
            priceNew: Number(objPlanos[1]?.valorNovoMensal),
            priceOldYear: objPlanos[1]?.valorAntigoAnual ? Number(objPlanos[1]?.valorAntigoAnual) : "",
            priceNewYear: objPlanos[1]?.valorNovoAnual ? Number(objPlanos[1]?.valorNovoAnual) : "",
            discountYear: objPlanos[1]?.descontoAnual ? Number(objPlanos[1]?.descontoAnual) : "",

        },
        {
            id: objPlanos[2]?.idPlano,
            title: objPlanos[2]?.nome,
            chips: [
                { label: "Faturamento da PJ até: R$ 150.000/mês", status: "sim" },
                { label: "Composição societária: até 3 sócios", status: "sim" },
                { label: "Atuação junto a planos de saúde (sob demanda): envio de NF por e-mail e anexos em portais", status: "sim" },
                { label: "Obrigações fiscais/contábeis: completas dentro do Simples Nacional", status: "sim" },
                { label: "Certificado digital incluso: apenas no plano anual", status: "sim" },
                { label: "Regime tributário: Simples Nacional", status: "sim" },
                { label: "IRPF sob demanda: até 3 sócios inclusos", status: "sim" },
                { label: "Emissão via Clarea Fácil: ilimitada", status: "sim" },
                { label: "Pró-labore dos sócios", status: "sim" },
                { label: "Emissões/mês feitas pela equipe Clarea: até 20", status: "sim" },
                { label: "Folha de pagamento: até 3 colaboradores", status: "sim" },
                { label: "50% de desconto em planejamento patrimonial para até 3 sócios (somente no plano anual)", status: "nao" },
            ],
            description: objPlanos[2]?.descricao,
            priceOld: objPlanos[2]?.valorAntigoMensal ? Number(objPlanos[2]?.valorAntigoMensal) : "",
            discount: objPlanos[2]?.descontoMensal ? Number(objPlanos[2]?.descontoMensal) : "",
            priceNew: Number(objPlanos[2]?.valorNovoMensal),
            priceOldYear: objPlanos[2]?.valorAntigoAnual ? Number(objPlanos[2]?.valorAntigoAnual) : "",
            priceNewYear: objPlanos[2]?.valorNovoAnual ? Number(objPlanos[2]?.valorNovoAnual) : "",
            discountYear: objPlanos[2]?.descontoAnual ? Number(objPlanos[2]?.descontoAnual) : "",

        },
        {
            id: objPlanos[3]?.idPlano,
            title: objPlanos[3]?.nome,
            chips: [
                { label: "Faturamento da PJ até: R$ 400.000/mês (R$ 4,8 milhões/ano – teto do Simples Nacional)", status: "sim" },
                { label: "Composição societária: até 3 sócios", status: "sim" },
                { label: "Atuação junto a planos de saúde (sob demanda): envio de NF por e-mail e anexos em portais", status: "sim" },
                { label: "Obrigações fiscais/contábeis: completas dentro do Simples Nacional", status: "sim" },
                { label: "Certificado digital incluso: apenas no plano anual", status: "sim" },
                { label: "Regime tributário: Simples Nacional", status: "sim" },
                { label: "IRPF sob demanda: até 3 sócios inclusos", status: "sim" },
                { label: "Emissão via Clarea Fácil: ilimitada", status: "sim" },
                { label: "Pró-labore dos sócios", status: "sim" },
                { label: "Emissões/mês feitas pela equipe Clarea: até 30", status: "sim" },
                { label: "Folha de pagamento: até 5 colaboradores", status: "sim" },
                { label: "50% de desconto em planejamento patrimonial para até 3 sócios (somente no plano anual)", status: "sim" },
            ],
            description: objPlanos[3]?.descricao,
            priceOld: objPlanos[3]?.valorAntigoMensal ? Number(objPlanos[3]?.valorAntigoMensal) : "",
            discount: objPlanos[3]?.descontoMensal ? Number(objPlanos[3]?.descontoMensal) : "",
            priceNew: Number(objPlanos[3]?.valorNovoMensal),
            priceOldYear: objPlanos[3]?.valorAntigoAnual ? Number(objPlanos[3]?.valorAntigoAnual) : "",
            priceNewYear: objPlanos[3]?.valorNovoAnual ? Number(objPlanos[3]?.valorNovoAnual) : "",
            discountYear: objPlanos[3]?.descontoAnual ? Number(objPlanos[3]?.descontoAnual) : "",

        },
        {
            id: objPlanos[4]?.idPlano,
            title: objPlanos[4]?.nome,
            chips: [
                { label: "Estrutura adaptada a regimes Lucro Presumido ou Lucro Real", status: "sim" },
                { label: "Planejamento tributário e societário completo", status: "sim" },
                { label: "Consultoria contábil e estratégica personalizada", status: "sim" },
                { label: "Suporte dedicado e reuniões sob demanda", status: "sim" },
            ],
            description: objPlanos[4]?.descricao,
            priceOld: objPlanos[4]?.valorAntigoMensal ? Number(objPlanos[4]?.valorAntigoMensal) : "",
            discount: objPlanos[4]?.descontoMensal ? Number(objPlanos[4]?.descontoMensal) : "",
            priceNew: Number(objPlanos[4]?.valorNovoMensal),
            priceOldYear: objPlanos[4]?.valorAntigoAnual ? Number(objPlanos[4]?.valorAntigoAnual) : "",
            priceNewYear: objPlanos[4]?.valorNovoAnual ? Number(objPlanos[4]?.valorNovoAnual) : "",
            discountYear: objPlanos[4]?.descontoAnual ? Number(objPlanos[4]?.descontoAnual) : "",

        }
    ];

    // Só exibe os planos que já vieram da API
    const planosVisiveis = plans.filter((plan) => plan.title);

    const HandleChoosePlan = (id, title, description, price, priceYear, periodicity) => {
        if (!token) {
            navigate("/Cadastro");
        }

        if (hasPlan) {
            navigate("../Dashboard/Assinatura");
            return;
        } else {
            console.log({ id, title, description, price, priceYear, periodicity });
            if (periodicity === "mensal") {
                localStorage.setItem("planoSelecionado", JSON.stringify({ id, title, description, price, periodicity }));
            } else {
                localStorage.setItem("planoSelecionado", JSON.stringify({ id, title, description, price: priceYear, periodicity }));
            }
            if (token) {
                fetchLogin();
                navigate("../checkout");
            } else {
                navigate("/Cadastro");
            }
            return;
        }

    }

    const [periodicity, setPeriodicity] = useState("anual");

    const handleChange = (event) => {
        setPeriodicity(event.target.value);
    };

    // Carrossel (apenas mobile): no desktop o container é uma grade e não rola
    const carrosselRef = useRef(null);
    const [slideAtivo, setSlideAtivo] = useState(0);

    const handleScrollCarrossel = () => {
        const container = carrosselRef.current;
        const primeiro = container?.firstElementChild;
        if (!primeiro) return;
        const passo = primeiro.offsetWidth + parseFloat(getComputedStyle(container).columnGap || 0);
        setSlideAtivo(Math.round(container.scrollLeft / passo));
    };

    const irParaSlide = (index) => {
        const slide = carrosselRef.current?.children[index];
        if (!slide) return;
        carrosselRef.current.scrollTo({ left: slide.offsetLeft - carrosselRef.current.offsetLeft, behavior: "smooth" });
    };

    return (
        <Box component="section" sx={{ width: "100%", display: "flex", justifyContent: "center", backgroundColor: "#f9f2e4", py: { xs: 8, md: 12 } }}>
            {/* Os cards ocupam quase toda a largura da tela (o cabeçalho tem largura própria) */}
            <Box sx={{ width: { xs: "90%", md: "96%" } }}>
                {/* Cabeçalho */}
                <Box sx={{ textAlign: "center", maxWidth: "1280px", mx: "auto", mb: { xs: 4, md: 5 } }}>
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.75,
                            py: 0.75,
                            mb: 2.5,
                            borderRadius: "999px",
                            backgroundColor: "rgba(255, 200, 69, 0.25)",
                            color: "#0b243d",
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                        }}
                    >
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#ffc845" }} />
                        Planos e preços
                    </Box>
                    <h2 style={{ margin: "0 0 12px", fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px", color: "#0b243d" }}>
                        {props.title}
                    </h2>
                    <Box component="p" sx={{ m: "0 auto", maxWidth: "620px", fontSize: { xs: "1rem", md: "1.05rem" }, lineHeight: 1.7, color: "#4b5563" }}>
                        Compare os planos e escolha o que melhor atende ao momento do seu consultório ou clínica.
                    </Box>

                    {/* Seletor de periodicidade */}
                    <Box
                        role="radiogroup"
                        aria-label="Periodicidade"
                        sx={{
                            mt: 3,
                            display: "inline-flex",
                            p: 0.5,
                            gap: 0.5,
                            backgroundColor: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                        }}
                    >
                        {[
                            { value: "mensal", label: "Mensal" },
                            { value: "anual", label: "Anual" },
                        ].map((opcao) => {
                            const ativo = periodicity === opcao.value;
                            return (
                                <Button
                                    key={opcao.value}
                                    role="radio"
                                    aria-checked={ativo}
                                    onClick={() => handleChange({ target: { value: opcao.value } })}
                                    sx={{
                                        minWidth: 110,
                                        textTransform: "none",
                                        fontSize: 14,
                                        fontWeight: 700,
                                        borderRadius: "9px",
                                        py: 1,
                                        color: ativo ? "#ffc845" : "#6b7280",
                                        backgroundColor: ativo ? "#0b243d" : "transparent",
                                        "&:hover": { backgroundColor: ativo ? "#0b243d" : "#f3f4f6" },
                                    }}
                                >
                                    {opcao.label}
                                </Button>
                            );
                        })}
                    </Box>
                </Box>

                {/* Cards dos planos */}
                <Box
                    ref={carrosselRef}
                    onScroll={handleScrollCarrossel}
                    sx={{
                        display: { xs: "flex", sm: "grid" },
                        gridTemplateColumns: { sm: "repeat(2, 1fr)", lg: "repeat(auto-fit, minmax(220px, 1fr))" },
                        gap: 2,
                        alignItems: "stretch",
                        // Mobile: carrossel horizontal com snap, ocupando a largura da tela
                        overflowX: { xs: "auto", sm: "visible" },
                        scrollSnapType: { xs: "x mandatory", sm: "none" },
                        mx: { xs: "-5.5%", sm: 0 },
                        px: { xs: "5.5%", sm: 0 },
                        pb: { xs: 1, sm: 0 },
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": { display: "none" },
                    }}
                >
                    {planosVisiveis.map((plan, i) => {
                        const isSobConsulta = plan.title === "Plus Personalizado";
                        const destaque = Boolean(plan.recommended);
                        const valorAntigo = periodicity === "mensal" ? plan.priceOld : plan.priceOldYear;
                        const desconto = periodicity === "mensal" ? plan.discount : plan.discountYear;
                        const valorAtual = periodicity === "mensal" ? plan.priceNew : plan.priceNewYear;

                        return (
                            <Box
                                key={plan.id ?? i}
                                sx={{ display: "flex", flex: { xs: "0 0 90%", sm: "initial" }, scrollSnapAlign: { xs: "center", sm: "none" } }}
                            >
                                <motion.div
                                    custom={i}
                                    variants={cardVariants}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.2 }}
                                    style={{ display: "flex", width: "100%" }}
                                >
                                    <Box
                                        sx={{
                                            ...cardSx,
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            position: "relative",
                                            backgroundColor: destaque ? "#0b243d" : "#fff",
                                            border: destaque ? "none" : "1px solid #e5e7eb",
                                            color: destaque ? "#fff" : "#111827",
                                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                            "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 20px rgba(0,0,0,0.08)" },
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 22 }}>
                                            {destaque ? (
                                                <Chip
                                                    label="Recomendado"
                                                    size="small"
                                                    sx={{ backgroundColor: "rgba(255, 200, 69, 0.15)", color: "#ffc845", fontWeight: 700, fontSize: 10, height: 20, letterSpacing: 0.6, textTransform: "uppercase" }}
                                                />
                                            ) : <span />}
                                            {!isSobConsulta && desconto > 0 && (
                                                <Chip
                                                    label={`${parseInt(desconto)}% OFF`}
                                                    size="small"
                                                    sx={{ backgroundColor: destaque ? "rgba(34, 197, 94, 0.15)" : "#dcfce7", color: destaque ? "#4ade80" : "#15803d", fontWeight: 700, fontSize: 10, height: 20 }}
                                                />
                                            )}
                                        </Box>

                                        <Typography sx={{ mt: 1.5, fontSize: 20, fontWeight: 700 }}>{plan.title}</Typography>
                                        <Typography sx={{ mt: 0.5, fontSize: 12, color: destaque ? "#cbd5e1" : "#6b7280", minHeight: 36 }}>
                                            {plan.description}
                                        </Typography>

                                        <Box sx={{ mt: 2, minHeight: 72 }}>
                                            {isSobConsulta ? (
                                                <Typography sx={{ fontSize: 24, fontWeight: 700, color: destaque ? "#ffc845" : "#111827" }}>Sob consulta</Typography>
                                            ) : (
                                                <>
                                                    {valorAntigo > 0 && (
                                                        <Typography sx={{ fontSize: 12, color: destaque ? "#94a3b8" : "#9ca3af", textDecoration: "line-through" }}>
                                                            {moeda(valorAntigo)}
                                                        </Typography>
                                                    )}
                                                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                                        <Typography sx={{ fontSize: 26, fontWeight: 700, color: destaque ? "#ffc845" : "#111827", lineHeight: 1.2 }}>
                                                            {moeda(valorAtual)}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 12, color: destaque ? "#94a3b8" : "#6b7280" }}>/mês</Typography>
                                                    </Box>
                                                    <Typography sx={{ fontSize: 11, color: destaque ? "#94a3b8" : "#9ca3af", mt: 0.25 }}>
                                                        {periodicity === "anual" ? "no plano anual" : "no plano mensal"}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>

                                        <Box sx={{ mt: 2 }}>
                                            {isSobConsulta ? (
                                                <Button
                                                    fullWidth
                                                    startIcon={<WhatsAppIcon />}
                                                    onClick={() => window.open(gerarLinkWhatsApp("Olá, tenho interesse no plano *Plus Personalizado* (Sob Consulta). Poderia me enviar mais informações?"))}
                                                    sx={{ textTransform: "none", fontSize: 13, fontWeight: 700, color: "#111827", backgroundColor: "#f3f4f6", borderRadius: "8px", py: 1, "&:hover": { backgroundColor: "#e5e7eb" } }}
                                                >
                                                    Solicitar contato
                                                </Button>
                                            ) : (
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    endIcon={<ArrowForwardIcon />}
                                                    onClick={() => HandleChoosePlan(plan.id, plan.title, plan.description, plan.priceNew, plan.priceNewYear, periodicity)}
                                                    sx={{ textTransform: "none", fontSize: 13, fontWeight: 700, color: "#111827", backgroundColor: "#ffc845", borderRadius: "8px", boxShadow: "none", py: 1, "&:hover": { backgroundColor: "#f5b82e", boxShadow: "none" } }}
                                                >
                                                    Assinar plano
                                                </Button>
                                            )}
                                        </Box>

                                        <Box sx={{ mt: 2.5, pt: 2, borderTop: destaque ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #f3f4f6", display: "flex", flexDirection: "column", gap: 1 }}>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: destaque ? "#94a3b8" : "#6b7280", mb: 0.5 }}>
                                                O que está incluso
                                            </Typography>
                                            {plan.chips.map((chip, idx) => (
                                                <ItemBeneficio key={idx} label={chip.label} status={chip.status} escuro={destaque} />
                                            ))}
                                        </Box>
                                    </Box>
                                </motion.div>
                            </Box>
                        );
                    })}
                </Box>

                {/* Indicadores do carrossel (apenas mobile) */}
                {planosVisiveis.length > 1 && (
                    <Box sx={{ mt: 1.5, display: { xs: "flex", sm: "none" }, alignItems: "center", justifyContent: "center", gap: 0.75 }}>
                        {planosVisiveis.map((plan, i) => (
                            <Box
                                key={plan.id ?? i}
                                component="button"
                                type="button"
                                aria-label={`Ver plano ${plan.title}`}
                                onClick={() => irParaSlide(i)}
                                sx={{
                                    width: slideAtivo === i ? 20 : 8,
                                    height: 8,
                                    p: 0,
                                    border: "none",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    backgroundColor: slideAtivo === i ? "#0b243d" : "#d1d5db",
                                    transition: "all 0.2s ease",
                                }}
                            />
                        ))}
                    </Box>
                )}

                {planosVisiveis.length > 0 && (
                    <>
                        {/* Legenda */}
                        <Box sx={{ mt: 2.5, display: "flex", alignItems: "center", gap: 2.5, flexWrap: "wrap", justifyContent: "center" }}>
                            <ItemBeneficio label="Incluso" status="sim" />
                            <ItemBeneficio label="Incluso com limite" status="limitado" />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CancelOutlinedIcon sx={{ fontSize: 16, color: "#d1d5db" }} />
                                <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>Não incluso</Typography>
                            </Box>
                        </Box>

                        {/* Garantias */}
                        <Box sx={{ mt: 4, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: { xs: 1.25, sm: 3 } }}>
                            {["Sem multa e sem fidelidade", "Acesso imediato à plataforma", "Suporte da nossa equipe"].map((item) => (
                                <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.9rem", color: "#0b243d", fontWeight: 600 }}>
                                    <CheckIcon sx={{ fontSize: 18, color: "#c8961e" }} />
                                    {item}
                                </Box>
                            ))}
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default Plans;
